import React, {useState} from "react";
import * as Yup from "yup";
import ValidateMessages from "../../../../../../../../core/models/static/texts/vallidate-messages";
import {makeRequired, makeValidate} from "mui-rff";
import AddProductTechnicalFileDropZone from "../../../../../../../components/app-specific/dropzone/add-product-technical-file";
import Api from "../../../../../../../../core/services/api/api";
import useIsMounted from "../../../../../../../hooks/use-is-mounted";
import {InputFileAcceptTypes, ProductTechnicalFileTypes} from "../../../../../../../../core/constants/enums";
import {readFile, ReadingFileAs} from "../../../../../../../../core/services/utils/utils";
import {Col} from "reactstrap";
import {OutlinedInputForm} from "../../../../../../../components/base/input/outlined_input";
import Button from "../../../../../../../components/base/button/button";
import Form from "../../../../../../../components/base/form";
import ToastService from "../../../../../../../../core/services/toast";
import {useParams} from "react-router-dom";
import UIUtils from "../../../../../../../utils";

const schema = Yup.object().shape({
    name: Yup.string().trim().nullable().required(ValidateMessages.required),
    description: Yup.string().trim().nullable(),
});

const validate = makeValidate(schema);
const required = makeRequired(schema);

const ProductInformationTechnicalFilesFormView = ({onSuccess}) => {
    const params = useParams()
    const [submitting, setSubmitting] = useState(false);
    const [file, setFile] = useState();
    const isMounted = useIsMounted();


    /**
     * Creates a technical file document for the current product.
     * @param {any} values
     * @param {formApi} form
     * @return {Promise<undefined>}
     */
    const createProductTechnicalFile = async (values, form) => {
        if (!file) {
            ToastService.error({
                title: "File Required",
                message: "You must submit a file before creating a technical file document.",
            });
            return;
        }
        /**@type {File}*/
        setSubmitting(true);
        const forApi = {
            partId: parseInt(params.id),
            name: values.name?.trim(),
            description: values.description?.trim(),
            extention: file.extension,
            contentBytes: file.contentBytes,
        }
        const response = await Api.createOperatorPartTechnicalFile(forApi);
        if (!isMounted()) return;
        setSubmitting(false);
        if (response?.resultFlag) {
            onSuccess(response.data);
            setFile(undefined);
            for (const name of ['name', 'description']) {
                form.change(name, undefined);
                form.resetFieldState(name);
            }
        }
    }

    /**
     * Sets the selected file in the local state so that it can be used upon form submission.
     * @param files {File[] | null}
     */
    const onFileSelected = async (files) => {
        const selected = files[0];
        const file = {
            name: selected.name,
            extension: selected.name?.split('.')[selected.name?.split('.').length - 1],
            contentBytes: btoa(await readFile(selected, ReadingFileAs.binaryString)),
            url: await readFile(selected, ReadingFileAs.dataUrl),
        }
        let type;
        switch (`.${file.extension}`) {
            case InputFileAcceptTypes.pdf:
                type = ProductTechnicalFileTypes.PDF;
                break;
            case InputFileAcceptTypes.html:
                type = ProductTechnicalFileTypes.HTML;
                break;
            default:
                const isImage = UIUtils.isImage(file.extension);
                const isVideo = UIUtils.isVideo(file.extension);
                if (isImage) {
                    type = ProductTechnicalFileTypes.IMAGE;
                } else if (isVideo) {
                    type = ProductTechnicalFileTypes.unknown;
                } else {
                    type = ProductTechnicalFileTypes.unknown;
                }
                break;
        }
        file['type'] = type;
        setFile(file);
    }

    return (
        <>
            <Form
                className='d-flex flex-wrap w-100'
                onSubmit={createProductTechnicalFile}
                validate={validate}
                initialValues={{}}
                render={({values, form}) => {
                    return (
                        <>
                            <Col xs={12} className={'mb-3'}>
                                <OutlinedInputForm
                                    name={'name'}
                                    placeholder={'title...'}
                                    label={"Name"}
                                    required={required.name}
                                    value={values.name ?? ''}
                                    onChange={(e) => form.change(e.target.name, e.target.value)}
                                />
                            </Col>
                            <Col xs={12} className={'mb-3'}>
                                <OutlinedInputForm
                                    name={'description'}
                                    placeholder={'description...'}
                                    label={"Description"}
                                    required={required.description}
                                    value={values.description ?? ''}
                                    multiline
                                    minRows={12}
                                    onChange={(e) => form.change(e.target.name, e.target.value)}
                                />
                            </Col>
                            <Col xs={12} className={'mb-3'}>
                                <AddProductTechnicalFileDropZone
                                    disabled={submitting}
                                    uploading={submitting}
                                    onSuccess={onFileSelected}
                                    file={file}
                                />
                            </Col>
                            <Col xs={12} className={'d-flex mb-4'}>
                                <Button
                                    className='py-2-5 px-5 m-auto'
                                    type={'submit'}
                                    color={'primary'}
                                    variant={'contained'}
                                    disabled={submitting}
                                >
                                    {
                                        submitting
                                            ? "Creating..."
                                            : "Create"
                                    }
                                </Button>
                            </Col>
                        </>
                    )
                }}
            />

        </>
    )
}

export default ProductInformationTechnicalFilesFormView;
